import {NameSchema, Roles} from "@/utils/constants/common.constants.ts";
import {Push} from "notivue";
import {z} from "zod";

export interface IAuthRequest {
  email: string,
  password: string
}

export interface ILoginByPhoneNumberRequest {
  phoneNumber: string,
  password: string
}

export interface IRegisterRequest extends IAuthRequest {
  name: string
}

export interface ICompletePhoneNumberRegistrationDetails extends Pick<ApiPayload, "completePhoneNumberRegistrationDetailsToken"> {
  name: string,
  classLevel: `${ClassLevel}`,
  password: string,
}

export interface ICompleteProfileForGoogleAccount extends IUser {
}

export interface ILoginOrRegisterByPhoneNumber {
  phoneNumber: string
}

export interface IValidateOTPRequest {
  otp: string
}

export interface IValidatePhoneNumberOTPRequest extends IValidateOTPRequest {
  phoneNumber: string
}

export interface IResetPasswordRequest {
  token: string,
  password: string
}


export interface IGetResult {
  messageType?: keyof Push,
  success?: boolean,
  message?: string,
  title?: string,
  data?: ApiPayload,
  redirectUrl?: string
}

export interface IRegisterResponse {
  email: string
}

export interface ITokenResponse {
  token: string
}

export interface IFetchCurrentUserResponse {
  user: Omit<IUser, "password">
}

export interface IChangePasswordRequest {
  oldPassword: string,
  newPassword: string
}

export interface IDetectPhoneNumberRequest extends Pick<ApiPayload, "completePhoneNumberRegistrationDetailsToken"> {
  phoneNumber: string
}

export interface ICloudinarySignatureResponse {
  timestamp: string,
  signature: string,
  apiKey: string,
  cloudName: string
}

export interface IUpdateProfileRequest extends IUser {

}

export interface ILoginByCodeAndPassword extends Pick<IUser, "code" | "password"> {
}

export enum PaymentStatus {
  PAID = "paid",
  REFUNDED = "refunded",
  CANCELED = "canceled"
}

export interface TimestampsConfig {
  createdAt: Date | string;
  updatedAt: Date | string;
}

export interface ISchema extends Document, AGGridConfig {

}

export interface IUser extends ISchema {
  _id: string,
  name: string;
  nickName?: string;
  code: string;
  email?: string;
  lastLogin: Date;
  status?: IAttendance['status'];
  createdBy: IUser;
  forceLogoutAt?: Date | undefined;
  role: Roles;
  provider?: Providers;
  avatar?: string;
  password?: string;
  groups?: {
    math?: IGroup | null,
    physics?: IGroup | null
  },
  isProfileCompleted: boolean;
  isVerified: boolean;
  classLevel?: ClassLevel | null,
}

export interface IGrade extends ISchema {
  _id:string,
  subject: Subject | null ,
  grade: number | null,
  name: string,
  maxGrade: number | null,
  student: IUser | null ,
  classLevel: ClassLevel
}

export interface IPayment extends ISchema {
  _id: string,
  student: IUser,
  amount: number,
  month: any,
  year: number,
  subject: Subject,
  addedBy: IUser,
  status: PaymentStatus,
  note?: string
}

export interface ILesson extends ISchema {
  _id: string,
  title?: string,
  subject: Subject | null,
  classLevel: ClassLevel | null,
  attendance: IAttendance[],
  group: IGroup | null
}

export interface IAttendance extends ISchema {
  _id: string,
  student: IUser,
  payments: IPayment[],
  status: AttendanceStatus,
  previousStatus: AttendanceStatus,
  note?: string
}

export interface IGroup extends ISchema {
  _id: string,
  name: string,
  subject: Subject,
  maxStudents: number,
  classLevel: ClassLevel
}

export interface AGGridConfig {
  _changedCells?: Record<string, boolean>;
  _original?: any
}

export interface Document extends TimestampsConfig {
  _id: string
}

export interface IExam extends ISchema{
  classLevel:ILesson['classLevel'],
  subject:ILesson['subject'],
  title: string,
  duration: number,
  totalMarks: number,
  questions: IQuestion[],
  createdBy: IUser,
  status: ExamStatus
}

export interface IQuestion extends ISchema {
  exam: IExam,
  type: QuestionType,
  text: string,
  options: string[],
  correctAnswer: number | boolean | undefined,
  marks: number,
}

export interface IExamResult extends ISchema {
  exam: IExam,
  student: IUser,
  answers: IAnswer[],
  totalMarksObtained: number,
  completedAt: Date
}

export interface IAnswer {
  question: IQuestion,
  answer: number | boolean,
  marksObtained: number
}

export enum QuestionType {
  MCQ = "MCQ",
  TRUE_FALSE = "TRUE_FALSE"
}

export enum TrueFalseAnswers {
  True = "true",
  False = "false"
}

export enum ExamStatus {
  DRAFT = "draft",
  PUBLISHED = "published",
  CLOSED = "closed"
}

export interface ApiPayload {
  phoneNumber?: string,
  user?: IUser,
  completePhoneNumberRegistrationDetailsToken?: string,
  resetPasswordToken?: string,
  function?: "initUser",
  message?: string,
  students?: IUser[],
  studentGrades?: IGrade[] | null,
  timestamp?: number,
  signature?: string,
  apiKey?: string,
  cloudName?: string,
  lessons?: ILesson[]
  lesson?: ILesson,
  lessonsForStudent?: Omit<ILesson, "attendance">[] | null,
  grades?: IGrade[],
  studentPayments?: IPayment[] | null
  payments?: IPayment[] | null,
  data?: any,
  forceUserToLogoutOut?: boolean
}

export enum Providers {
  Local = "local", Google = "google", Admin = "admin"
}

export enum AttendanceStatus {
  Present = "حاضر",
  Absent = "غايب",
  Late = "متأخر",
  Excused = "معذور",
  Initial = "لا يوجد",
  WrongGroup = "حاضر في مجموعة أخرى",
}

export interface ViewAllStudentsResponse {

}

export enum ClassLevel {
  FirstSecondary = "الصف الأول الثانوي",
  SecondSecondary = "الصف الثاني الثانوي",
  ThirdSecondary = "الصف الثالث الثانوي"
}

export enum Subject {
  Math = "الرياضيات",
  Physics = "الفيزياء"
}

export interface ISubmitGradeRequest extends IGrade{
}

export interface ICreateLessonRequest extends Omit<Omit<ILesson, keyof TimestampsConfig>, "_id" | "attendance"> {
}

export interface ICreatePaymentRequest extends Omit<IPayment, "addedBy"> {
}

export interface ICreateGroupRequest extends IGroup {
}

export interface ICreateExamRequest extends IExam {
}

export interface ISubmitExamAnswersRequest {
  answers: IAnswer[],
  examId:string
}


export interface IAddAttendanceRequest {
  student: Pick<IUser, "_id" | "name" | "groups">,
  status?: `${AttendanceStatus}`
  lessonId: string,
}

export interface IDeleteAttendanceRequest {
  attendanceId:string
  lessonId: string
}

export interface IUpdateStudentRequest extends Partial<IUser> {
  groups: IUser['groups']
}

export interface ICreateStudentRequest extends Pick<IUser, "name" | "classLevel" | "isProfileCompleted" | "groups" | "password"> {
}

export type QueryFilter<T> = {
  filter?: Partial<T>,
  search?: string,
  limit?: number,
  page?: number
}

export const TimestampsSchema = z.object({
  createdAt: z.union([z.string().datetime(), z.date()]),
  updatedAt: z.union([z.string().datetime(), z.date()]),
});

export const GroupSchema = TimestampsSchema.extend({
  _id: z.string(),
  name: NameSchema,
  subject: z.nativeEnum(Subject),
  maxStudents: z.number().int().positive(),
  classLevel: z.nativeEnum(ClassLevel),
});

export const UserSchema: z.ZodType<IUser> = TimestampsSchema.extend({
  _id: z.string(),
  name: z.string(),
  nickName: z.string().optional(),
  code: z.string(),
  email: z.string().email().optional(),
  lastLogin: z.date(),
  status: z.nativeEnum(AttendanceStatus).optional(),
  _changedCells: z.array(z.string()).optional(),
  _original: z.any().optional(),
  createdBy: z.lazy(() => UserSchema),
  forceLogoutAt: z.union([z.date(), z.undefined()]).optional(),
  role: z.nativeEnum(Roles),
  provider: z.nativeEnum(Providers).optional(),
  avatar: z.string().optional(),
  password: z.string().optional(),
  groups: z
    .object({
      math: GroupSchema.nullable().optional(),
      physics: GroupSchema.nullable().optional(),
    })
    .optional(),
  isProfileCompleted: z.boolean(),
  isVerified: z.boolean(),
  classLevel: z.nativeEnum(ClassLevel).nullable().optional(),
});

// Payment schema
export const PaymentSchema = TimestampsSchema.extend({
  _id: z.string(),
  student: UserSchema,
  amount: z.number(),
  month: z.any(),
  _changedCells: z.record(z.boolean()),
  year: z.number(),
  subject: z.nativeEnum(Subject),
  addedBy: UserSchema,
  status: z.nativeEnum(PaymentStatus),
  note: z.string().optional(),
});

// Attendance schema
export const AttendanceSchema = TimestampsSchema.extend({
  _id: z.string(),
  student: UserSchema,
  payments: z.array(PaymentSchema),
  status: z.nativeEnum(AttendanceStatus),
  previousStatus: z.nativeEnum(AttendanceStatus),
  note: z.string().optional(),
});

// Lesson schema
export const LessonSchema = TimestampsSchema.extend({
  _id: z.string(),
  title: z.string().optional(),
  subject: z.nativeEnum(Subject).nullable(),
  classLevel: z.nativeEnum(ClassLevel).nullable(),
  attendance: z.array(AttendanceSchema),
  group: GroupSchema.nullable(),
});